import { useCallback, useEffect, useRef, useState } from "react"
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk"
import {
  AudioSource,
  ConnectionStatus,
  LANGUAGE_LOCALES,
  TranscriptEntry,
  TranscriptSpeaker,
} from "@/modules/speech-to-text/types"
import { SofiaEnvironment } from "@/sofia-environment"
import useTranscriptStore from "@/stores/useTranscriptStore"
import { Language } from "@/types/__generated__/union-types"

export type UseSpeechToTextOptions = {
  language: Language
  audioSource: AudioSource
  speaker: TranscriptSpeaker
  deviceId?: string | null
  enabled?: boolean
  onTranscript?: (text: string, isFinal: boolean, speaker: TranscriptSpeaker) => void
  onError?: (error: Error) => void
  onStatusChange?: (status: ConnectionStatus) => void
}

export type UseSpeechToTextReturn = {
  status: ConnectionStatus
  transcripts: TranscriptEntry[]
  startRecording: () => Promise<void>
  startRecordingWithStream: (stream: MediaStream) => Promise<void>
  stopRecording: () => Promise<void>
  clearTranscripts: () => void
}

// Map Language enum to SpeechToText endpoint keys
const LANGUAGE_TO_ENDPOINT_KEY: Record<Language, "en" | "de" | "fr" | "it"> = {
  ENGLISH: "en",
  GERMAN: "de",
  FRENCH: "fr",
  ITALIAN: "it",
}

// Audio format configuration for PCM wave format
export const AUDIO_SAMPLE_RATE = SofiaEnvironment.SpeechToText.SampleRate
const AUDIO_BITS_PER_SAMPLE = 16
const AUDIO_CHANNELS = 1

export const useSpeechToText = (options: UseSpeechToTextOptions): UseSpeechToTextReturn => {
  const { language, audioSource, speaker, deviceId, enabled = true, onTranscript, onError, onStatusChange } = options

  const [status, setStatus] = useState<ConnectionStatus>("disconnected")

  const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null)
  const pushStreamRef = useRef<SpeechSDK.PushAudioInputStream | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const workletNodeRef = useRef<AudioWorkletNode | null>(null)
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const deviceIdRef = useRef<string | null | undefined>(deviceId)

  // Keep deviceIdRef in sync
  deviceIdRef.current = deviceId

  const transcriptStore = useTranscriptStore()

  // Update status and notify callbacks
  const updateStatus = useCallback(
    (newStatus: ConnectionStatus) => {
      console.log(`[STT-${speaker}] Status: ${newStatus}`)
      setStatus(newStatus)
      transcriptStore.setConnectionStatus(speaker, newStatus)
      onStatusChange?.(newStatus)
    },
    [speaker, transcriptStore, onStatusChange],
  )

  // Create speech config with extended timeouts
  const createSpeechConfig = useCallback(() => {
    const endpointKey = LANGUAGE_TO_ENDPOINT_KEY[language]
    const baseEndpoint = SofiaEnvironment.SpeechToText.Endpoints[endpointKey]
    const endpoint = new URL(baseEndpoint)
    endpoint.searchParams.set("language", LANGUAGE_LOCALES[language])
    endpoint.searchParams.set("format", "simple")

    const speechConfig = SpeechSDK.SpeechConfig.fromEndpoint(endpoint)
    speechConfig.speechRecognitionLanguage = LANGUAGE_LOCALES[language]

    // Configure timeout settings to prevent early EndOfDictation
    // speechConfig.setProperty(SpeechSDK.PropertyId.Speech_SegmentationSilenceTimeoutMs, "5000")
    speechConfig.setProperty(SpeechSDK.PropertyId.SpeechServiceConnection_InitialSilenceTimeoutMs, "60000")

    console.log(`[STT-${speaker}] Created speech config with language: ${LANGUAGE_LOCALES[language]}`)
    return speechConfig
  }, [language, speaker])

  // Setup recognizer event handlers
  const setupRecognizerEvents = useCallback(
    (recognizer: SpeechSDK.SpeechRecognizer) => {
      recognizer.recognizing = (_, e) => {
        if (e.result.text) {
          console.log(`[STT-${speaker}] Recognizing: ${e.result.text}`)
        }
      }

      recognizer.recognized = (_, e) => {
        const { result } = e
        if (result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          const { text } = result
          if (text) {
            console.log(`[STT-${speaker}] Recognized: ${text}`)
            transcriptStore.addTranscript(speaker, {
              text,
              timestamp: Date.now(),
              isFinal: true,
              speaker,
            })
            onTranscript?.(text, true, speaker)
          }
        } else if (result.reason === SpeechSDK.ResultReason.NoMatch) {
          const noMatchDetail = SpeechSDK.NoMatchDetails.fromResult(result)
          console.log(`[STT-${speaker}] NoMatch reason: ${SpeechSDK.NoMatchReason[noMatchDetail.reason]}`, {
            reason: noMatchDetail.reason,
            reasonName: SpeechSDK.NoMatchReason[noMatchDetail.reason],
            resultId: result.resultId,
            offset: result.offset,
            duration: result.duration,
            json: result.json,
          })
        } else {
          console.log(`[STT-${speaker}] Recognized event with reason: ${SpeechSDK.ResultReason[result.reason]}`, {
            reason: result.reason,
            reasonName: SpeechSDK.ResultReason[result.reason],
            resultId: result.resultId,
            json: result.json,
          })
        }
      }

      recognizer.canceled = (_, e) => {
        console.log(`[STT-${speaker}] Canceled: ${SpeechSDK.CancellationReason[e.reason]}`)
        if (e.reason === SpeechSDK.CancellationReason.Error) {
          console.error(`[STT-${speaker}] Error: ${e.errorDetails}`)
          onError?.(new Error(e.errorDetails))
        }
        updateStatus("disconnected")
        transcriptStore.setIsRecording(speaker, false)
      }

      recognizer.sessionStarted = (_, e) => {
        console.log(`[STT-${speaker}] Session started: ${e.sessionId}`)
        updateStatus("connected")
        transcriptStore.setIsRecording(speaker, true)
      }

      recognizer.sessionStopped = (_, e) => {
        console.log(`[STT-${speaker}] Session stopped: ${e.sessionId}`)
        updateStatus("disconnected")
        transcriptStore.setIsRecording(speaker, false)
      }
    },
    [speaker, transcriptStore, onTranscript, onError, updateStatus],
  )

  // Setup audio processing to feed push stream using AudioWorklet
  const setupAudioProcessing = useCallback(
    async (stream: MediaStream, pushStream: SpeechSDK.PushAudioInputStream) => {
      const audioContext = new AudioContext({ sampleRate: AUDIO_SAMPLE_RATE })
      audioContextRef.current = audioContext

      // Load the audio worklet processor
      await audioContext.audioWorklet.addModule("/audio-processor.js")

      const source = audioContext.createMediaStreamSource(stream)
      const workletNode = new AudioWorkletNode(audioContext, "audio-processor")
      workletNodeRef.current = workletNode

      // Handle PCM data from the worklet
      workletNode.port.onmessage = (event) => {
        pushStream.write(event.data)
      }

      source.connect(workletNode)
      workletNode.connect(audioContext.destination)
      console.log(`[STT-${speaker}] Audio processing pipeline connected (AudioWorklet)`)
    },
    [speaker],
  )

  // Start recording from microphone
  const startRecording = useCallback(async () => {
    console.log(`[STT-${speaker}] startRecording called, deviceId:`, deviceIdRef.current)

    // For remoteTrack audio source, use startRecordingWithStream instead
    if (audioSource === "remoteTrack") {
      console.warn(
        `[STT-${speaker}] startRecording called with remoteTrack audio source. Use startRecordingWithStream instead.`,
      )
      return
    }

    try {
      updateStatus("connecting")

      const speechConfig = createSpeechConfig()
      let audioConfig: SpeechSDK.AudioConfig

      if (audioSource === "microphone") {
        // Get microphone stream with optional device selection
        const currentDeviceId = deviceIdRef.current
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
            sampleRate: AUDIO_SAMPLE_RATE,
            ...(currentDeviceId && currentDeviceId !== "default" && { deviceId: { exact: currentDeviceId } }),
          },
        })
        mediaStreamRef.current = micStream

        // Create push stream for custom audio input
        const audioFormat = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(
          AUDIO_SAMPLE_RATE,
          AUDIO_BITS_PER_SAMPLE,
          AUDIO_CHANNELS,
        )
        const pushStream = SpeechSDK.AudioInputStream.createPushStream(audioFormat)
        pushStreamRef.current = pushStream
        audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream)

        // Setup audio processing to feed the push stream
        await setupAudioProcessing(micStream, pushStream)
      } else {
        // TranscriptSpeaker/System audio - requires screen share with push stream
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
        })
        mediaStreamRef.current = stream

        const audioFormat = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(
          AUDIO_SAMPLE_RATE,
          AUDIO_BITS_PER_SAMPLE,
          AUDIO_CHANNELS,
        )
        const pushStream = SpeechSDK.AudioInputStream.createPushStream(audioFormat)
        pushStreamRef.current = pushStream
        audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream)

        // Setup audio processing
        await setupAudioProcessing(stream, pushStream)
      }

      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig)
      recognizerRef.current = recognizer
      setupRecognizerEvents(recognizer)

      // Start continuous recognition
      recognizer.startContinuousRecognitionAsync(
        () => {
          console.log(`[STT-${speaker}] Continuous recognition started`)
        },
        (err) => {
          console.error(`[STT-${speaker}] Error starting recognition:`, err)
          onError?.(new Error(String(err)))
          updateStatus("disconnected")
        },
      )
    } catch (error) {
      console.error(`[STT-${speaker}] startRecording error:`, error)
      const err = error instanceof Error ? error : new Error(String(error))
      onError?.(err)
      updateStatus("disconnected")
      throw err
    }
  }, [audioSource, speaker, createSpeechConfig, setupRecognizerEvents, setupAudioProcessing, updateStatus, onError])

  // Start recording with a provided MediaStream (for remote audio tracks from Twilio)
  const startRecordingWithStream = useCallback(
    async (stream: MediaStream) => {
      console.log(`[STT-${speaker}] startRecordingWithStream called`)
      try {
        updateStatus("connecting")
        mediaStreamRef.current = stream

        const speechConfig = createSpeechConfig()

        // Create push stream for custom audio input
        const audioFormat = SpeechSDK.AudioStreamFormat.getWaveFormatPCM(
          AUDIO_SAMPLE_RATE,
          AUDIO_BITS_PER_SAMPLE,
          AUDIO_CHANNELS,
        )
        const pushStream = SpeechSDK.AudioInputStream.createPushStream(audioFormat)
        pushStreamRef.current = pushStream

        const audioConfig = SpeechSDK.AudioConfig.fromStreamInput(pushStream)

        // Setup audio processing to feed the push stream
        await setupAudioProcessing(stream, pushStream)

        const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig)
        recognizerRef.current = recognizer
        setupRecognizerEvents(recognizer)

        // Start continuous recognition
        recognizer.startContinuousRecognitionAsync(
          () => {
            console.log(`[STT-${speaker}] Continuous recognition started (from stream)`)
          },
          (err) => {
            console.error(`[STT-${speaker}] Error starting recognition:`, err)
            onError?.(new Error(String(err)))
            updateStatus("disconnected")
          },
        )
      } catch (error) {
        console.error(`[STT-${speaker}] startRecordingWithStream error:`, error)
        const err = error instanceof Error ? error : new Error(String(error))
        onError?.(err)
        updateStatus("disconnected")
        throw err
      }
    },
    [speaker, createSpeechConfig, setupRecognizerEvents, setupAudioProcessing, updateStatus, onError],
  )

  // Stop recording
  const stopRecording = useCallback(async () => {
    console.log(`[STT-${speaker}] stopRecording called`)

    if (recognizerRef.current) {
      await new Promise<void>((resolve) => {
        recognizerRef.current?.stopContinuousRecognitionAsync(
          () => {
            console.log(`[STT-${speaker}] Recognition stopped`)
            recognizerRef.current?.close()
            recognizerRef.current = null
            resolve()
          },
          (err) => {
            console.error(`[STT-${speaker}] Error stopping recognition:`, err)
            recognizerRef.current?.close()
            recognizerRef.current = null
            resolve()
          },
        )
      })
    }

    if (pushStreamRef.current) {
      console.log(`[STT-${speaker}] Closing push stream`)
      pushStreamRef.current.close()
      pushStreamRef.current = null
    }

    if (workletNodeRef.current) {
      console.log(`[STT-${speaker}] Disconnecting worklet node`)
      workletNodeRef.current.disconnect()
      workletNodeRef.current = null
    }

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      console.log(`[STT-${speaker}] Closing AudioContext`)
      await audioContextRef.current.close()
      audioContextRef.current = null
    }

    if (mediaStreamRef.current) {
      console.log(`[STT-${speaker}] Stopping media stream tracks`)
      mediaStreamRef.current.getTracks().forEach((track) => track.stop())
      mediaStreamRef.current = null
    }

    transcriptStore.setIsRecording(speaker, false)
    updateStatus("disconnected")
    console.log(`[STT-${speaker}] stopRecording completed`)
  }, [speaker, updateStatus, transcriptStore])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recognizerRef.current?.close()
      pushStreamRef.current?.close()
      workletNodeRef.current?.disconnect()
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close()
      }
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    }
  }, [])

  // Handle device change - restart recording when deviceId changes while recording
  const previousDeviceIdRef = useRef<string | null | undefined>(deviceId)

  useEffect(() => {
    const previousDeviceId = previousDeviceIdRef.current
    previousDeviceIdRef.current = deviceId

    // Only restart if device changed and currently recording (recognizer exists)
    if (previousDeviceId !== deviceId && recognizerRef.current && audioSource === "microphone") {
      console.log(`[STT-${speaker}] Device changed from ${previousDeviceId} to ${deviceId}, restarting...`)

      const restart = async () => {
        await stopRecording()
        await startRecording()
      }

      restart().catch((error) => {
        console.error(`[STT-${speaker}] Failed to restart with new device:`, error)
      })
    }
  }, [deviceId, audioSource, speaker, stopRecording, startRecording])

  // Return no-op functions when speech-to-text is disabled
  if (!enabled) {
    return {
      status: "disconnected",
      transcripts: [],
      startRecording: async () => {},
      startRecordingWithStream: async () => {},
      stopRecording: async () => {},
      clearTranscripts: () => {},
    }
  }

  return {
    status,
    transcripts: transcriptStore.getMergedTranscripts(),
    startRecording,
    startRecordingWithStream,
    stopRecording,
    clearTranscripts: () => transcriptStore.clearTranscripts(speaker),
  }
}
